/**
 * ImageCard - Grid card for image library
 * Uses optimized Cloudinary thumbnails with lazy loading
 * Supports premium image blur/lock for free users
 * Memoized to prevent unnecessary re-renders and duplicate image loads
 */
import { useState, memo } from "react";
import { ImageOff, Lock, Sparkles } from "lucide-react";

export const ImageCard = memo(function ImageCard({ image, onClick, isFree }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const isPremiumLocked = isFree && image.is_premium;

  // Use pre-generated thumbnail URL if available, otherwise generate it
  const thumbnailUrl = image.thumbnail_url || image.secure_url.replace(
    "/upload/",
    "/upload/c_fill,w_300,h_225,q_auto:low,f_auto/"
  );

  return (
    <div
      className="group relative rounded-lg overflow-hidden bg-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.1)] transition-all duration-300 cursor-pointer"
      data-testid={`image-card-${image.public_id}`}
      onClick={() => onClick(image)}
    >
      <div className="aspect-[4/3] relative">
        {!loaded && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
            <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
          </div>
        )}
        {error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-100 text-slate-400">
            <ImageOff className="h-8 w-8 mb-1" />
            <span className="text-xs">Failed to load</span>
          </div>
        ) : (
          <img
            src={thumbnailUrl}
            alt={image.dish_name}
            loading="lazy"
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
            className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
              loaded ? "opacity-100" : "opacity-0"
            } ${isPremiumLocked ? "blur-[6px]" : ""}`}
            draggable={false}
            style={{ userSelect: "none", WebkitUserSelect: "none" }}
          />
        )}

        {/* Premium lock overlay */}
        {isPremiumLocked && loaded && !error && (
          <div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center z-10" data-testid={`premium-lock-${image.public_id}`}>
            <div className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center mb-2 shadow-sm">
              <Lock className="h-4.5 w-4.5 text-slate-600" />
            </div>
            <span className="text-[11px] font-semibold text-white drop-shadow-md mb-1">
              Locked Premium Image
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-white/90 backdrop-blur-sm text-slate-700 shadow-sm">
              <Sparkles className="h-2.5 w-2.5 text-orange-500" />
              Unlock with Studio Pass 2.0
            </span>
          </div>
        )}

        {/* Badge - Zomato | Swiggy Approved */}
        {!isPremiumLocked && (
          <div className="absolute top-2 left-2 z-10">
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold bg-black/90 backdrop-blur-sm">
              <span className="text-[#E23744]">Zomato</span>
              <span className="text-[#E5E5E5] mx-1">|</span>
              <span className="text-[#FC8019]">Swiggy</span>
              <span className="text-[#E5E5E5] ml-1.5">Approved</span>
            </span>
          </div>
        )}
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
        <p className="text-sm font-medium text-white drop-shadow-md truncate">
          {image.dish_name}
        </p>
        {image.veg_nonveg && (
          <p className="text-xs text-white/80 truncate mt-0.5">
            {image.veg_nonveg}
          </p>
        )}
      </div>
    </div>
  );
});
